import type { SceneTracks, TimelineTrack } from "@/timeline/types";
import { rippleShiftElements } from "./shift";

export interface RippleAdjustment {
	trackId: string;
	/** See rippleShiftElements (shift.ts) for exactly how this is compared per direction. */
	boundaryTime: number;
	/** Signed — see rippleShiftElements (shift.ts) for what each sign means per direction. */
	delta: number;
	direction: "after" | "before";
}

export function applyRippleAdjustments({
	tracks,
	adjustments,
}: {
	tracks: SceneTracks;
	adjustments: RippleAdjustment[];
}): SceneTracks {
	if (adjustments.length === 0) {
		return tracks;
	}

	const adjustmentsByTrack = new Map<string, RippleAdjustment[]>();
	for (const adjustment of adjustments) {
		const trackAdjustments = adjustmentsByTrack.get(adjustment.trackId) ?? [];
		trackAdjustments.push(adjustment);
		adjustmentsByTrack.set(adjustment.trackId, trackAdjustments);
	}

	return {
		overlay: tracks.overlay.map((track) =>
			applyTrackRippleAdjustments({
				track,
				adjustments: adjustmentsByTrack.get(track.id) ?? [],
			}),
		),
		main: applyTrackRippleAdjustments({
			track: tracks.main,
			adjustments: adjustmentsByTrack.get(tracks.main.id) ?? [],
		}),
		audio: tracks.audio.map((track) =>
			applyTrackRippleAdjustments({
				track,
				adjustments: adjustmentsByTrack.get(track.id) ?? [],
			}),
		),
	};
}

function applyTrackRippleAdjustments<
	TElement extends TimelineTrack["elements"][number],
	TTrack extends TimelineTrack & { elements: TElement[] },
>({
	track,
	adjustments,
}: {
	track: TTrack;
	adjustments: RippleAdjustment[];
}): TTrack {
	if (adjustments.length === 0) {
		return track;
	}

	// "after" adjustments first, furthest boundary first — closing the rightmost gap before a
	// closer one keeps each subsequent comparison consistent with already-shifted positions.
	// "before" adjustments second, nearest (leftmost) boundary first — the mirror image, since
	// they push content forward instead of pulling it back. The two passes target disjoint
	// element sets for any single edit (after touches startTime >= boundary, before touches
	// endTime <= a different boundary), so running all "after" adjustments before any "before"
	// ones is safe rather than needing to interleave them.
	const afterAdjustments = adjustments
		.filter((adjustment) => adjustment.direction === "after")
		.sort((a, b) => b.boundaryTime - a.boundaryTime);
	const beforeAdjustments = adjustments
		.filter((adjustment) => adjustment.direction === "before")
		.sort((a, b) => a.boundaryTime - b.boundaryTime);

	let elements: TElement[] = track.elements;
	for (const adjustment of [...afterAdjustments, ...beforeAdjustments]) {
		elements = rippleShiftElements({
			elements,
			boundaryTime: adjustment.boundaryTime,
			delta: adjustment.delta,
			direction: adjustment.direction,
		});
	}

	return { ...track, elements };
}
